import {
  OpenPositionListItem,
  SnipePositionStatus,
} from 'api/types/httpsTypes/dex-trade';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import Checkbox from '../../../../components/inputs/Checkbox';
import { TokenIconWithChain } from 'modules/shared-components/asset/token-icon';
import Tooltip from 'modules/shared-components/tooltip';
import { TableColumn } from 'modules/shared-components/data-display/table/types';
import React from 'react';
import {
  CommandLineIcon,
  CreditCardIcon,
  DocumentDuplicateIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { CopyContent } from 'modules/shared-components/tooltip/copy-content';
import { IconButton } from 'modules/shared-components/button/IconButton';
import { formatAddress } from 'modules/ethereum/utils';
import Button from 'modules/shared-components/button/button';
import { Tabs } from '../../../../components/layout/SIDEBAR_CONST';
import { usePageParams } from '../../../../wallets/components/utils-new';

interface TokenHeaderProps {
  selectedTokens: string[];
  setSelectedTokens: React.Dispatch<React.SetStateAction<string[]>>;
  trades: OpenPositionListItem[] | undefined;
}

export const TokenColumnHeader = ({
  selectedTokens,
  setSelectedTokens,
  trades,
}: TokenHeaderProps) => {
  const { allSellableSelected, hasSellable } = useMemo(() => {
    const sellable =
      trades?.filter((item) => SnipePositionStatus[item.status]?.canSell) || [];
    const selected = sellable.every((item) => selectedTokens.includes(item.id));
    return { allSellableSelected: selected, hasSellable: sellable.length > 0 };
  }, [selectedTokens, trades]);

  const selectAll = useCallback(() => {
    if (!trades) return;
    const sellable = trades
      .filter((item) => SnipePositionStatus[item.status]?.canSell)
      .map((item) => item.id);
    setSelectedTokens(sellable);
  }, [trades, setSelectedTokens]);

  const deselectAll = useCallback(() => {
    setSelectedTokens([]);
  }, [setSelectedTokens]);

  useEffect(() => {
    if (!trades || selectedTokens.length === 0) return;

    const tradeIds = new Set(trades.map((t) => t.id));
    const filteredSelection = selectedTokens.filter((id) => tradeIds.has(id));

    if (filteredSelection.length !== selectedTokens.length) {
      setSelectedTokens(filteredSelection);
    }
  }, [trades, selectedTokens, setSelectedTokens]);

  return (
    <Tooltip text="The Token address, chain, and DEX.">
      <div className="flex items-center space-x-1">
        {hasSellable ? (
          <Checkbox
            checked={allSellableSelected}
            onChange={(e) => (e.target.checked ? selectAll() : deselectAll())}
          />
        ) : (
          <div />
        )}
        <div className="xxs:text-dex-white-secondary hover:text-dex-white">
          Token
        </div>
      </div>
    </Tooltip>
  );
};

interface TokenColumnProps {
  selectedTokens: string[];
  trades: OpenPositionListItem[];
  setSelectedTokens: React.Dispatch<React.SetStateAction<string[]>>;
}

/* eslint-disable react/prop-types */

export const TokenColumn = ({
  trades,
  selectedTokens,
  setSelectedTokens,
}: TokenColumnProps): TableColumn<OpenPositionListItem> => ({
  label: (
    <TokenColumnHeader
      trades={trades} // You can pass this too if needed
      selectedTokens={selectedTokens}
      setSelectedTokens={setSelectedTokens}
    />
  ),
  key: 'TOKEN',
  component: React.memo(function TokenCell({ data }) {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setSelectedTokens((prev) =>
        e.target.checked
          ? [...prev, data.id]
          : prev.filter((t) => t !== data.id)
      );
    };
    const [params, setAddress] = usePageParams();

    return (
      <div className="flex items-center xxs:gap-2">
        <div className="flex space-x-2 items-center">
          {SnipePositionStatus[data.status]?.canSell && (
            <Checkbox
              className="z-auto"
              checked={selectedTokens.includes(data.id)}
              onClick={(e) => e.stopPropagation()}
              onChange={handleChange}
            />
          )}
          <TokenIconWithChain
            hasIcon={data.hasIcon}
            size="lg"
            className="w-9 h-9"
            tokenName={data.tokenName}
            address={data.tokenAddress}
            symbol={data.tokenSymbol}
            protocol={data.protocol?.protocol}
            chain={data.chain}
          />
        </div>
        <div className="max-w-[120px] overflow-hidden truncate flex flex-col">
          <div className="flex items-center xxs:space-x-1">
            <div className="font-bold text-sm">{data.tokenSymbol}</div>
            <CopyContent text={data.tokenAddress}>
              <IconButton as="button">
                <DocumentDuplicateIcon className="xxs:text-dex-white-secondary hover:text-dex-white w-4 h-4" />
              </IconButton>
            </CopyContent>
          </div>
          <div className="xxs:space-x-1 xxs:items-center flex">
            {data.volatilityBotId && (
              <Tooltip text="Volatility Bot">
                <div>
                  <CommandLineIcon className="w-4 h-4 xxs:text-dex-white-secondary" />
                </div>
              </Tooltip>
            )}
            {data.copyTradingBotId && (
              <Tooltip text="Wallet Bot">
                <div>
                  <CreditCardIcon className="w-4 h-4 xxs:text-dex-white-secondary" />
                </div>
              </Tooltip>
            )}
            {data.telegramSnipingCallEvent && (
              <Tooltip text="Telegram Bot">
                <div>
                  <PhoneIcon className="w-4 h-4 xxs:text-dex-white-secondary" />
                </div>
              </Tooltip>
            )}

            <Tooltip text="Your Wallet">
              <div>
                <Button
                  className=" xxs:text-dex-white-secondary hover:text-dex-white xxs:p-0 xxs:m-0 flex justify-center items-center "
                  type="button"
                  onClick={(e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAddress({
                      ...params,
                      walletAddress: data.vaultAddress,
                      tab: Tabs.Portfolio,
                    });
                  }}
                >
                  <div className="flex gap-x-1 items-center">
                    <CreditCardIcon className="w-4 h-4 " />
                    <div className="normal-case font-normal xxs:text-xs">
                      {formatAddress(data.vaultAddress, 2)}
                    </div>
                  </div>
                </Button>
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  }) as ({ data }: { data: OpenPositionListItem }) => JSX.Element,
});
